import React, { useEffect, useMemo, useState } from "react";
import { useAusencias, Ausencia, TipoAusencia, TIPO_AUSENCIA_LABEL, TIPO_AUSENCIA_COLOR, AusenciaDocumento } from "@/contexts/AusenciasContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, X, Paperclip, Settings2, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { ResumenAusencias } from "@/components/ausencias/ResumenAusencias";

interface Tecnico { user_id: string; nombre: string; email: string; rol: string }

export default function AusenciasEquipo() {
  const { perfil } = useAuth();
  const { ausencias, cupos, modoComputo, aprobarAusencia, rechazarAusencia, upsertCupo, listDocumentos, getSignedUrl, refresh } = useAusencias();
  const modoLabel = modoComputo === "naturales" ? "Días naturales" : "Días laborables";
  const defaultDias = modoComputo === "naturales" ? 30 : 22;

  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [filterTecnico, setFilterTecnico] = useState<string>("all");
  const [filterTipo, setFilterTipo] = useState<string>("all");
  const [filterEstado, setFilterEstado] = useState<string>("pendiente");
  const [rechazoOpen, setRechazoOpen] = useState<Ausencia | null>(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [docsView, setDocsView] = useState<{ ausencia: Ausencia; docs: AusenciaDocumento[] } | null>(null);
  const [cupoOpen, setCupoOpen] = useState(false);
  const [cupoTecnico, setCupoTecnico] = useState<string>("");
  const [cupoAnio, setCupoAnio] = useState<number>(new Date().getFullYear());
  const [cupoDias, setCupoDias] = useState<number>(defaultDias);

  useEffect(() => { setCupoDias(defaultDias); }, [defaultDias]);


  useEffect(() => {
    if (!perfil?.empresa_id) return;
    supabase
      .from("perfiles")
      .select("user_id, nombre, email, rol")
      .eq("empresa_id", perfil.empresa_id)
      .then(({ data }) => setTecnicos((data || []) as Tecnico[]));
  }, [perfil?.empresa_id]);

  const filtradas = useMemo(() => {
    return ausencias.filter((a) => {
      if (filterTecnico !== "all" && a.tecnico_id !== filterTecnico) return false;
      if (filterTipo !== "all" && a.tipo !== filterTipo) return false;
      if (filterEstado !== "all" && a.estado_validacion !== filterEstado) return false;
      return true;
    });
  }, [ausencias, filterTecnico, filterTipo, filterEstado]);

  const handleAprobar = async (a: Ausencia) => {
    if (a.tipo === "baja_medica") {
      const docs = await listDocumentos(a.id);
      if (docs.length === 0) {
        toast({ title: "Falta justificante", description: "Una baja médica requiere documento adjunto", variant: "destructive" });
        return;
      }
    }
    await aprobarAusencia(a.id);
  };

  const handleRechazar = async () => {
    if (!rechazoOpen || !motivoRechazo.trim()) return;
    await rechazarAusencia(rechazoOpen.id, motivoRechazo.trim());
    setRechazoOpen(null);
    setMotivoRechazo("");
  };

  const openDocs = async (a: Ausencia) => {
    const docs = await listDocumentos(a.id);
    setDocsView({ ausencia: a, docs });
  };

  const viewDoc = async (path: string) => {
    const url = await getSignedUrl(path);
    if (url) window.open(url, "_blank");
  };

  const handleSaveCupo = async () => {
    if (!cupoTecnico) return;
    await upsertCupo(cupoTecnico, cupoAnio, cupoDias);
    setCupoOpen(false);
    setCupoTecnico("");
    setCupoDias(22);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Ausencias del equipo</h1>
          <p className="text-sm text-muted-foreground">Aprueba o rechaza las ausencias solicitadas</p>
        </div>
        <Button variant="outline" onClick={() => setCupoOpen(true)} className="gap-2">
          <Settings2 className="h-4 w-4" /> Cupo de vacaciones
        </Button>
      </div>

      <Tabs defaultValue="solicitudes" className="space-y-4">
        <TabsList>
          <TabsTrigger value="solicitudes">Solicitudes</TabsTrigger>
          <TabsTrigger value="resumen">Resumen</TabsTrigger>
        </TabsList>
        <TabsContent value="resumen">
          <ResumenAusencias tecnicos={tecnicos} />
        </TabsContent>
        <TabsContent value="solicitudes" className="space-y-4">
      <Card>
        <CardContent className="p-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Select value={filterTecnico} onValueChange={setFilterTecnico}>
            <SelectTrigger><SelectValue placeholder="Técnico" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los técnicos</SelectItem>
              {tecnicos.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {(["vacaciones", "baja_medica", "asuntos_propios", "formacion"] as TipoAusencia[]).map((t) => (
                <SelectItem key={t} value={t}>{TIPO_AUSENCIA_LABEL[t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterEstado} onValueChange={setFilterEstado}>
            <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="pendiente">Pendientes</SelectItem>
              <SelectItem value="aprobada">Aprobadas</SelectItem>
              <SelectItem value="rechazada">Rechazadas</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {filtradas.length === 0 && (
          <Card><CardContent className="py-8 text-center text-muted-foreground">No hay ausencias con estos filtros</CardContent></Card>
        )}
        {filtradas.map((a) => (
          <Card key={a.id}>
            <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={TIPO_AUSENCIA_COLOR[a.tipo]}>{TIPO_AUSENCIA_LABEL[a.tipo]}</Badge>
                  <span className="font-semibold">{a.tecnico?.nombre || a.tecnico_id.slice(0, 8)}</span>
                  <span className="text-sm">
                    {format(new Date(a.fecha_inicio), "dd/MM/yyyy")} – {format(new Date(a.fecha_fin), "dd/MM/yyyy")}
                  </span>
                  <span className="text-sm text-muted-foreground">({Number(a.dias_solicitados)}d)</span>
                  {a.estado_validacion === "aprobada" && <Badge variant="outline" className="border-green-500 text-green-700">Aprobada</Badge>}
                  {a.estado_validacion === "rechazada" && <Badge variant="destructive">Rechazada</Badge>}
                  {a.estado_validacion === "pendiente" && <Badge variant="outline" className="border-amber-500 text-amber-700">Pendiente</Badge>}
                </div>
                {a.motivo && <p className="text-sm text-muted-foreground mt-1">{a.motivo}</p>}
                {a.estado_validacion === "rechazada" && a.motivo_rechazo && (
                  <p className="text-sm text-destructive mt-1">Motivo rechazo: {a.motivo_rechazo}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => openDocs(a)} className="gap-1">
                  <Paperclip className="h-3.5 w-3.5" /> Justificantes
                </Button>
                {a.estado_validacion !== "aprobada" && (
                  <Button size="sm" onClick={() => handleAprobar(a)} className="gap-1">
                    <Check className="h-3.5 w-3.5" /> Aprobar
                  </Button>
                )}
                {a.estado_validacion !== "rechazada" && (
                  <Button size="sm" variant="destructive" onClick={() => { setRechazoOpen(a); setMotivoRechazo(""); }} className="gap-1">
                    <X className="h-3.5 w-3.5" /> Rechazar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
        </TabsContent>
      </Tabs>


      <Dialog open={!!rechazoOpen} onOpenChange={(v) => { if (!v) setRechazoOpen(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar ausencia</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivo del rechazo</Label>
            <Textarea value={motivoRechazo} onChange={(e) => setMotivoRechazo(e.target.value)} rows={3} placeholder="Explica al técnico por qué se rechaza" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRechazoOpen(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleRechazar} disabled={!motivoRechazo.trim()}>Rechazar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!docsView} onOpenChange={(v) => { if (!v) setDocsView(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Justificantes</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {docsView?.docs.length === 0 && <p className="text-sm text-muted-foreground">Sin justificantes adjuntos</p>}
            {docsView?.docs.map((d) => (
              <button key={d.id} onClick={() => viewDoc(d.url_archivo)} className="w-full flex items-center gap-2 border rounded p-2 hover:bg-muted text-left">
                <Paperclip className="h-4 w-4" />
                <span className="truncate">{d.nombre_archivo}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cupoOpen} onOpenChange={setCupoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Cupo de vacaciones
              <Badge variant="secondary" className="text-xs">{modoLabel}</Badge>
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="config">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="config">Configurar</TabsTrigger>
              <TabsTrigger value="lista">Cupos actuales</TabsTrigger>
            </TabsList>
            <TabsContent value="config" className="space-y-3">
              <div>
                <Label>Técnico</Label>
                <Select value={cupoTecnico} onValueChange={setCupoTecnico}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {tecnicos.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Año</Label>
                  <Input type="number" value={cupoAnio} onChange={(e) => setCupoAnio(Number(e.target.value))} />
                </div>
                <div>
                  <Label>Días totales</Label>
                  <Input type="number" value={cupoDias} onChange={(e) => setCupoDias(Number(e.target.value))} step="0.5" />
                </div>
              </div>
              <Button onClick={handleSaveCupo} className="w-full">Guardar</Button>
            </TabsContent>
            <TabsContent value="lista">
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {cupos.length === 0 && <p className="text-sm text-muted-foreground">Sin cupos configurados (default {defaultDias} {modoComputo === "naturales" ? "días naturales" : "días laborables"})</p>}
                {cupos.map((c) => {
                  const t = tecnicos.find((x) => x.user_id === c.tecnico_id);
                  return (
                    <div key={c.id} className="flex items-center justify-between border rounded p-2 text-sm">
                      <div>
                        <p className="font-medium">{t?.nombre || c.tecnico_id.slice(0, 8)}</p>
                        <p className="text-xs text-muted-foreground">Año {c.anio}</p>
                      </div>
                      <Badge variant="outline">{Number(c.dias_totales)} {modoComputo === "naturales" ? "días naturales" : "días laborables"}</Badge>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
